'use client';

import React from 'react';
import { ComparisonGridComponent } from '../types/content-components.types.js';
import { ComponentIcon } from './ComponentIcon.js';
import { useComponentAnimation } from './hooks/useComponentAnimation.js';

interface ComparisonGridProps {
  component: ComparisonGridComponent;
}

export const ComparisonGrid: React.FC<ComparisonGridProps> = ({ component }) => {
  const { props } = component;
  const { ref, isVisible } = useComponentAnimation(props.animation);

  const getItemStyles = (variant?: string) => {
    switch (variant) {
      case 'primary':
        // Muted Sage
        return 'bg-learn-sage-50 border-learn-sage-400 border-l-4';
      case 'success':
        // Growth Green (muted)
        return 'bg-green-50 border-green-400 border-l-4';
      case 'warning':
        // Muted Honey
        return 'bg-learn-honey-50 border-learn-honey-400 border-l-4';
      case 'danger':
        return 'bg-red-50 border-red-400 border-l-4';
      case 'info':
        // Muted Sky Blue
        return 'bg-learn-sky-50 border-learn-sky-400 border-l-4';
      case 'highlight':
        // Muted Clay/Coral
        return 'bg-learn-coral-50 border-learn-clay-400 border-l-4';
      default:
        // Soft neutral
        return 'bg-learn-sage-50 border-learn-sage-300 border-l-4';
    }
  };

  const getTextColor = (variant?: string) => {
    switch (variant) {
      case 'primary':
        return 'text-emerald-900';
      case 'success':
        return 'text-green-900';
      case 'warning':
        return 'text-yellow-900';
      case 'danger':
        return 'text-red-900';
      case 'info':
        return 'text-blue-900';
      case 'highlight':
        return 'text-purple-900';
      default:
        return 'text-gray-900';
    }
  };

  return (
    <div ref={ref} className="w-full space-y-4">
      {props.title && (
        <h3 className="text-xl font-bold text-gray-900 mb-4">{props.title}</h3>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {props.items.map((item, index) => (
          <div
            key={`${component.id}-item-${index}`}
            className={`${getItemStyles(item.variant)} rounded-lg p-5 transition-all duration-200 hover:shadow-md ${
              isVisible ? 'animate-fadeIn' : ''
            }`}
            style={{
              animationDelay: props.animation === 'stagger' ? `${index * 100}ms` : '0ms',
              opacity: isVisible ? undefined : 0
            }}
          >
            {/* Badge or Label */}
            {item.badge && (
              <div className="inline-block px-2 py-1 bg-white bg-opacity-70 rounded-full text-xs font-semibold text-gray-700 mb-3">
                {item.badge}
              </div>
            )}

            {/* Icon + Title */}
            <div className="flex items-start gap-3 mb-3">
              {item.icon && (
                <div className="flex-shrink-0 mt-1">
                  <ComponentIcon icon={item.icon} className="w-5 h-5" />
                </div>
              )}
              <div className="flex-1">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                  {item.label}
                </div>
                <h4 className={`text-lg font-bold ${getTextColor(item.variant)} mb-2`}>
                  {item.title}
                </h4>
              </div>
            </div>

            {/* Content */}
            <p className="text-sm text-gray-700 leading-relaxed">
              {item.content}
            </p>
          </div>
        ))}
      </div>

      {/* VS Divider (optional, shows between items on desktop) */}
      {props.showVsDivider && props.items.length === 2 && (
        <div className="hidden md:flex absolute inset-y-0 left-1/2 transform -translate-x-1/2 items-center justify-center z-10">
          <div className="bg-white px-3 py-1 rounded-full shadow-lg border-2 border-gray-300">
            <span className="text-sm font-bold text-gray-600">VS</span>
          </div>
        </div>
      )}
    </div>
  );
};

